import { useContext, useEffect, useRef, useState } from "react";
import { getTourCarousels } from "./house-service";
import { Carousel, CarouselItems, CarouselMedia } from "./types";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/a11y";
import { HouseCompetitionData } from "./contextProvider";
import Skeleton from "react-loading-skeleton";
import { A11y, Navigation } from "swiper/modules";
import DOMPurify from "dompurify";

const PhotoTour = () => {
  const [carousel, setCarousel] = useState<Carousel[]>();
  const [swiperData, setSwiperData] = useState<CarouselMedia[]>([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const swiperRef = useRef(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { prizeId } = useContext(HouseCompetitionData);

  useEffect(() => {
    if (prizeId) {
      getPhotoTourData();
    }
  }, [prizeId]);

  const getPhotoTourData = async () => {
    try {
      const response = await getTourCarousels(prizeId);
      const { Items } = (await response.json()) as CarouselItems;
      setCarousel(Items);
      setSwiperData(Items[0].Media);
    } catch (err) {
      console.log(`Error while fetching the carousel data - ${JSON.stringify(err)}`);
    } finally {
      setIsLoading(false);
    }
  };

  const onTabClick = (item: Carousel, tabIndex: number) => {
    setSwiperData(item.Media);
    setActiveTabIndex(tabIndex);
    setActiveSlideIndex(0);
    // Reset Swiper slide index to 0
    if (swiperRef.current) {
      swiperRef.current.slideTo(0); // Resets Swiper to first slide
    }
  };

  return (
    <div className="flex flex-col">
      <h2 className="text-left font-jost text-[#28282B] text-[16px] font-medium normal-case">Photo Tour</h2>
      {isLoading ? (
        <Skeleton inline highlightColor="#fff" height={500} containerClassName="w-full bg-[#f1f1f1]" />
      ) : (
        <>
          <div className="flex gap-6 items-center overflow-auto whitespace-nowrap">
            {carousel?.map((item, index) => (
              <div
                key={index}
                className={`self-start font-jost text-[16px] font-normal py-3 cursor-pointer ${activeTabIndex === index ? "border-b-2 border-solid border-b-[#FF8200] text-[#FF8200]" : "text-[#696971]"}`}
                onClick={() => onTabClick(item, index)}>
                {item.Name}
              </div>
            ))}
          </div>
          <div className="w-full relative carousel-container-within-slide-navigation">
            <Swiper
              modules={[Navigation, A11y]}
              slidesPerView={1}
              loop={false}
              spaceBetween={0}
              navigation
              onSlideChangeTransitionEnd={swiper => {
                setActiveSlideIndex(swiper.realIndex);
              }}
              onSwiper={swiper => (swiperRef.current = swiper)}
              className="w-full"
              style={{
                zIndex: 0,
              }}>
              {swiperData.map((mediaItem, index) => (
                <SwiperSlide key={index} className="mt-5 rounded-2xl border border-solid" style={{ height: "auto" }}>
                  <div
                    className="w-full h-full flex overflow-hidden relative rounded-lg"
                    style={{
                      boxShadow: "0px 1.5px 8px 0px rgba(0, 0, 0, 0.08)",
                    }}>
                    {!mediaItem.IsVideo ? (
                      <img src={mediaItem.Url} alt={`slide-${index}`} className="w-full" loading="lazy" />
                    ) : (
                      <video src={mediaItem.Url} className="h-full" autoPlay loop muted playsInline />
                    )}
                    {swiperData.length > 1 && (
                      <span className="absolute bottom-5 left-5 bg-[#00000080] rounded text-white px-1">
                        {activeSlideIndex + 1}/{swiperData.length}
                      </span>
                    )}
                  </div>
                </SwiperSlide>
              ))}
              {carousel?.[activeTabIndex]?.Description && (
                <div
                  className="w-full p-2 mt-3"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(carousel?.[activeTabIndex]?.Description),
                  }}
                />
              )}
            </Swiper>
          </div>
        </>
      )}
    </div>
  );
};

export default PhotoTour;
